import type { CurrentUser } from "@oshimap/domain";
import type { TokenStore } from "./token-store";

/**
 * 게스트가 된 이유.
 *
 * `MeResult` 는 "토큰이 틀렸다" 와 "지금은 확인할 수 없다" 를 이미 구분하는데,
 * 상태로 옮기면서 둘 다 `guest` 로 뭉개면 그 구분이 **읽는 쪽에서 사라진다.**
 * 그러면 화면은 서버가 죽었을 때도 "로그인해 주세요" 라고 말한다 —
 * 알 수 없는 것을 단정하는 것이고, 그 말을 따라 로그아웃하면 세션만 잃는다.
 */
export type GuestReason =
  | "no-token"       /* 처음부터 토큰이 없다 */
  | "signed-out"     /* 사용자가 직접 나갔다 */
  | "invalid-token"  /* 401/403 — 다시 로그인하면 된다 */
  | "unverifiable";  /* 네트워크·5xx — 로그인 문제가 아니다. 토큰은 지킨다 */

export type SessionState =
  | { status: "loading" }
  | { status: "guest"; reason: GuestReason }
  | { status: "authenticated"; user: CurrentUser };

export type SessionListener = (s: SessionState) => void;

/**
 * `fetchMe` 의 결과.
 *
 * "토큰이 틀렸다" 와 "지금은 확인할 수 없다" 는 전혀 다른 상황인데,
 * 둘 다 null 로 뭉개면 서버가 잠깐 죽은 사이에 사용자를 로그아웃시키고
 * 토큰까지 지운다 — 그리고 서버가 죽었으니 다시 로그인할 수도 없다.
 */
export type MeResult =
  | { kind: "user"; user: CurrentUser }
  | { kind: "invalid" }      /* 401/403 — 토큰을 버려야 한다 */
  | { kind: "unavailable" }; /* 네트워크·5xx — 지금은 알 수 없다. 토큰은 지킨다 */

export interface SessionPort {
  /** 토큰으로 현재 사용자를 조회한다. */
  fetchMe(token: string): Promise<MeResult>;
  /** 서버 측 토큰 폐기. 실패해도 로컬 정리는 진행한다 (SA-05). */
  revoke(token: string): Promise<void>;
}

/**
 * 플랫폼 무관 세션. 만료 감지와 재인증 유도를 한 곳에서 처리한다 (SA-04).
 * N5: 로그인 후 원래 하려던 동작을 이어서 수행한다.
 */
export class Session {
  #state: SessionState = { status: "loading" };
  #listeners = new Set<SessionListener>();
  #pending: (() => void) | null = null;
  #epoch = 0;
  #restoring: Promise<SessionState> | null = null;
  #revalidating: Promise<void> | null = null;
  #storage: Promise<unknown> = Promise.resolve();

  constructor(private store: TokenStore, private port: SessionPort) {}

  get state(): SessionState { return this.#state; }
  get user(): CurrentUser | null {
    return this.#state.status === "authenticated" ? this.#state.user : null;
  }

  subscribe(fn: SessionListener): () => void {
    this.#listeners.add(fn);
    fn(this.#state);
    return () => { this.#listeners.delete(fn); };
  }

  restore(): Promise<SessionState> {
    if (this.#restoring) return this.#restoring;
    const epoch = this.#epoch;
    const task = this.#restore(epoch).finally(() => {
      if (this.#restoring === task) this.#restoring = null;
    });
    this.#restoring = task;
    return task;
  }

  async #restore(epoch: number): Promise<SessionState> {
    try {
      const token = await this.#withStorage(() => this.store.get());
      if (epoch !== this.#epoch) return this.#state;
      if (!token) return this.#emit({ status: "guest", reason: "no-token" });
      const result = await this.port.fetchMe(token);
      if (epoch !== this.#epoch) return this.#state;
      if (result.kind === "user") return this.#emit({ status: "authenticated", user: result.user });
      if (result.kind === "invalid") {
        await this.invalidate(token);
        return this.#state;
      }
    } catch { /* 저장소·네트워크 장애도 loading 상태를 끝냅니다. 토큰은 추측해 지우지 않습니다. */ }
    return epoch === this.#epoch ? this.#emit({ status: "guest", reason: "unverifiable" }) : this.#state;
  }

  /**
   * 이 토큰이 아직 쓸 수 있는지만 물어봅니다.
   *
   * 서버는 만료된 토큰에 **403** 을 줍니다 — 401 이 아닙니다 (09 §AC-15).
   * 403 은 "권한이 없다" 와 글자가 같아서, 화면은 둘을 구분할 수 없습니다.
   * 그래서 한 시간 뒤에 돌아온 사람에게 앱은 **여전히 로그인 상태로 보이고**,
   * 누르는 것마다 실패하며, 로그인하라는 안내도 나오지 않습니다.
   *
   * `restore` 와 다른 점이 요점입니다: 확인에 **실패하면 아무것도 바꾸지
   * 않습니다.** `restore` 는 확인 못 한 것을 게스트로 내리는데, 요청 하나가
   * 실패할 때마다 그러면 잠깐의 망 장애가 로그아웃이 됩니다.
   * 서버가 "이 토큰은 못 쓴다" 고 분명히 말할 때만 정리합니다.
   */
  async revalidate(expectedToken?: string | null): Promise<void> {
    if (this.#state.status !== "authenticated") return;
    /* 계정이 바뀌면 앞 계정의 확인은 버립니다 (`signIn`·`signOut` 이 비웁니다) —
       그러지 않으면 새 계정의 만료가 앞사람 질문에 묻혀 넘어갑니다. */
    if (this.#revalidating) return this.#revalidating;
    const epoch = this.#epoch;
    const task = (async () => {
      const token = await this.#withStorage(() => this.store.get()).catch(() => null);
      if (!token || epoch !== this.#epoch) return;
      if (expectedToken !== undefined && token !== expectedToken) return;
      const result = await this.port.fetchMe(token).catch((): MeResult => ({ kind: "unavailable" }));
      if (epoch !== this.#epoch) return;
      /* 살아 있으면 그대로 둡니다 — 권한이 없을 뿐입니다. 상태를 다시 알려
         봐야 구독자만 흔듭니다. */
      if (result.kind === "invalid") await this.invalidate(token);
    })().finally(() => {
      if (this.#revalidating === task) this.#revalidating = null;
    });
    this.#revalidating = task;
    return task;
  }

  async signIn(token: string, user: CurrentUser): Promise<void> {
    const epoch = ++this.#epoch;
    this.#restoring = null;
    this.#revalidating = null;
    this.#emit({ status: "loading" });
    try {
      await this.#withStorage(() => epoch === this.#epoch ? this.store.set(token) : Promise.resolve());
    } catch (error) {
      if (epoch === this.#epoch) this.#emit({ status: "guest", reason: "unverifiable" });
      throw error;
    }
    if (epoch !== this.#epoch) return;
    this.#emit({ status: "authenticated", user });
    this.#runPending();
  }

  /**
   * 토큰은 그대로 두고 사용자 정보만 갈아끼운다 (닉네임 변경 등).
   * 다시 로그인시키지 않는 것이 요점이다.
   */
  refresh(user: CurrentUser): void {
    if (this.#state.status !== "authenticated" || user.id !== this.#state.user.id) return;
    this.#emit({ status: "authenticated", user });
  }

  async signOut(): Promise<void> {
    ++this.#epoch;
    this.#restoring = null;
    this.#revalidating = null;
    this.#pending = null;
    this.#emit({ status: "guest", reason: "signed-out" });
    const token = await this.#withStorage(async () => {
      const value = await this.store.get();
      await this.store.clear();
      return value;
    }).catch(() => null);
    if (token) { try { await this.port.revoke(token); } catch { /* 로컬 정리는 계속한다 */ } }
  }

  /** 401 을 받았을 때 API Client 가 호출한다 (SA-06). */
  async invalidate(expectedToken?: string | null): Promise<void> {
    const epoch = this.#epoch;
    await this.#withStorage(async () => {
      if (epoch !== this.#epoch) return;
      if (expectedToken !== undefined && await this.store.get() !== expectedToken) return;
      if (epoch !== this.#epoch) return;
      ++this.#epoch;
      this.#restoring = null;
      this.#emit({ status: "guest", reason: "invalid-token" });
      await this.store.clear();
    }).catch(() => { /* UI의 인증 상태는 이미 폐기했습니다. */ });
  }

  /** SecureStore의 늦은 쓰기·삭제가 다음 계정의 토큰을 덮지 않도록 순서를 보존합니다. */
  #withStorage<T>(action: () => Promise<T>): Promise<T> {
    const next = this.#storage.then(action);
    this.#storage = next.catch(() => undefined);
    return next;
  }

  /** 게스트가 로그인이 필요한 동작을 시도했을 때. 로그인 성공 시 자동 재실행된다. */
  requireAuth(action: () => void): boolean {
    if (this.#state.status === "authenticated") { action(); return true; }
    this.#pending = action;
    return false;
  }
  cancelPending(): void { this.#pending = null; }
  get hasPending(): boolean { return this.#pending !== null; }

  #runPending(): void {
    const a = this.#pending;
    this.#pending = null;
    if (a) a();
  }
  #emit(s: SessionState): SessionState {
    this.#state = s;
    for (const l of this.#listeners) l(s);
    return s;
  }
}
